import { useState } from "react";
import { toast } from "sonner";
import { RotateCcw, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useContentStore, resolveContent } from "@/context/ContentContext";
import { CONTENT_PAGES } from "@/data/siteContent";

const input = "w-full bg-transparent border border-darkgrey/40 px-4 py-3 font-body focus:outline-none focus:border-ink";
const PRESETS = [["S", 16], ["M", 28], ["L", 48], ["XL", 80]];
const PAGE_ROUTES = { "Home": "/", "Chi Sono": "/chi-sono", "Servizi": "/servizi", "Contatti": "/contatti" };

function FieldEditor({ field, current, reload }) {
  const [v, setV] = useState({
    text: current.text,
    size: current.size || field.size || 24,
    weight: current.weight || "normal",
    italic: !!current.italic,
    align: current.align || "left",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/content/${field.key}`, { ...v, size: Number(v.size) || null });
      toast.success("Testo salvato");
      reload();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    try {
      await api.delete(`/content/${field.key}`);
      toast.success("Testo ripristinato");
      reload();
      const d = resolveContent(field.key, {});
      setV({ text: d.text, size: d.size || field.size || 24, weight: d.weight, italic: d.italic, align: d.align });
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="border border-darkgrey/30 bg-paper/40 p-5 space-y-4" data-testid={`content-field-${field.key}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono2 text-[11px] uppercase tracking-widest text-darkgrey">{field.label}</span>
        <button type="button" onClick={reset} title="Ripristina originale"
          className="inline-flex items-center gap-1 font-mono2 text-[10px] uppercase text-darkgrey hover:text-glitch-red">
          <RotateCcw size={12} /> Ripristina
        </button>
      </div>

      <textarea data-testid={`content-text-${field.key}`} rows={v.text.length > 90 ? 3 : 2} value={v.text}
        onChange={(e) => setV({ ...v, text: e.target.value })}
        className={input + " text-sm leading-relaxed"} placeholder="Testo…" />

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="font-mono2 text-[10px] uppercase text-darkgrey">Dimensione</span>
          {PRESETS.map(([lbl, px]) => (
            <button key={lbl} type="button" onClick={() => setV({ ...v, size: px })}
              className={`px-2.5 py-1 border font-mono2 text-[10px] uppercase transition-colors ${Number(v.size) === px ? "bg-ink text-greige border-ink" : "border-darkgrey/40 hover:border-ink"}`}>
              {lbl}
            </button>
          ))}
          <input type="number" data-testid={`content-size-${field.key}`} value={v.size} min={8} max={200}
            onChange={(e) => setV({ ...v, size: e.target.value })}
            className="w-20 bg-transparent border border-darkgrey/40 px-2 py-1 font-mono2 text-xs focus:outline-none focus:border-ink" />
          <span className="font-mono2 text-[10px] text-darkgrey">px</span>
        </div>

        <button type="button" onClick={() => setV({ ...v, weight: v.weight === "bold" ? "normal" : "bold" })}
          className={`px-3 py-1 border font-mono2 text-xs transition-colors ${v.weight === "bold" ? "bg-ink text-greige border-ink" : "border-darkgrey/40 hover:border-ink"}`}><b>B</b></button>
        <button type="button" onClick={() => setV({ ...v, italic: !v.italic })}
          className={`px-3 py-1 border font-mono2 text-xs italic transition-colors ${v.italic ? "bg-ink text-greige border-ink" : "border-darkgrey/40 hover:border-ink"}`}>I</button>

        <select value={v.align} onChange={(e) => setV({ ...v, align: e.target.value })}
          className="bg-transparent border border-darkgrey/40 px-2 py-1 font-mono2 text-xs focus:outline-none focus:border-ink">
          <option value="left">Sinistra</option>
          <option value="center">Centro</option>
          <option value="right">Destra</option>
        </select>

        <button type="button" data-testid={`content-save-${field.key}`} onClick={save} disabled={busy}
          className="ml-auto bg-ink text-greige px-5 py-2 uppercase text-[11px] tracking-widest hover:bg-darkgrey transition-colors disabled:opacity-50">
          {busy ? "Salvo…" : "Salva"}
        </button>
      </div>

      <p className="font-heading leading-tight text-ink/90 pt-1"
        style={{ fontSize: Math.min(Number(v.size) || 24, 40), fontWeight: v.weight === "bold" ? 700 : 400, fontStyle: v.italic ? "italic" : "normal", textAlign: v.align }}>
        {v.text || "Anteprima"}
      </p>
    </div>
  );
}

export function ContentManager() {
  const { overrides, reload } = useContentStore();
  const pages = Object.keys(CONTENT_PAGES);
  const [openPage, setOpenPage] = useState(pages[0]);

  return (
    <div data-testid="content-manager" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {pages.map((p) => (
          <button key={p} data-testid={`content-page-${p}`} onClick={() => setOpenPage(p)}
            className={`px-4 py-2 font-mono2 text-[11px] uppercase tracking-widest border transition-colors ${openPage === p ? "bg-ink text-greige border-ink" : "border-darkgrey/40 hover:border-ink"}`}>
            {p}
          </button>
        ))}
        <a href={PAGE_ROUTES[openPage]} target="_blank" rel="noopener noreferrer"
          data-testid="content-view-page"
          className="ml-auto inline-flex items-center gap-2 px-4 py-2 font-mono2 text-[11px] uppercase tracking-widest border border-ink hover:bg-ink hover:text-greige transition-colors">
          <ExternalLink size={13} /> Vedi la pagina
        </a>
      </div>
      <div className="space-y-4 pt-2">
        {CONTENT_PAGES[openPage].map((field) => (
          <FieldEditor key={field.key} field={field} current={resolveContent(field.key, overrides)} reload={reload} />
        ))}
      </div>
    </div>
  );
}
